import type { ClientResource } from '@majuscule/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import type { JSX } from 'react';
import { PlatformChargesSection, platformChargesQuery } from './overview/PlatformChargesSection';
import { PageChrome } from '@/components/page-chrome';
import { Callout } from '@/components/ui/alert';
import { Card, CardBar, CardContent, CardFoot, CardHead } from '@/components/ui/card';
import { ChartLegend, HBars } from '@/components/ui/chart';
import { EmptyState, LoadingState } from '@/components/ui/empty';
import { Row4, Split21 } from '@/components/ui/layout-grid';
import { MonthSwitcher, PageHead, formatMonthLabel } from '@/components/ui/page-header';
import { StatCard } from '@/components/ui/stat-card';
import {
  Cell2,
  Table,
  TableBody,
  TableCell,
  TableEmptyRow,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  rowLinkClass,
} from '@/components/ui/table';
import { TableTabs } from '@/components/ui/tabs';
import { internalFetch } from '@/lib/api';
import {
  currentMonth,
  formatEurCents,
  formatEurosCents,
  formatPercent,
  marginPercent,
} from '@/lib/format';

interface OverviewLine {
  clientId: string | null;
  billed: string;
  providerCost: string;
  chargesBilled: string;
  chargesCost: string;
  platformCost: string;
  adjustments: string;
  net: string;
  margin: string;
  requestCount: number;
  billableCount: number;
}
interface OverviewResponse {
  month: string;
  byClient: OverviewLine[];
  total: OverviewLine;
}

type DetailTab = 'clients' | 'costs';

/** Spoken form for chart summaries (« 12,32 euros »). */
function spokenEuros(value: string | number): string {
  return `${formatEurosCents(value)} euros`;
}

export function OverviewPage(): JSX.Element {
  const [month, setMonth] = useState(currentMonth());
  const [tab, setTab] = useState<DetailTab>('clients');

  const overview = useQuery({
    queryKey: ['overview', month],
    queryFn: () => internalFetch<OverviewResponse>(`/internal/overview?month=${month}`),
  });
  const clients = useQuery({
    queryKey: ['clients'],
    queryFn: () => internalFetch<{ clients: ClientResource[] }>('/internal/clients'),
  });
  const charges = useQuery(platformChargesQuery);

  const names = new Map((clients.data?.clients ?? []).map((client) => [client.id, client.name]));
  const clientName = (id: string | null): string => (id === null ? '—' : (names.get(id) ?? id));

  const total = overview.data?.total;
  const totalPct = total === undefined ? 0 : marginPercent(total.net, total.margin);
  const rows = overview.data?.byClient ?? [];
  const chargeRows = charges.data?.charges ?? [];
  const activeCharges = chargeRows.filter((c) => c.active).length;
  const monthLabel = formatMonthLabel(month);

  const netItems = [...rows]
    .sort((a, b) => Number(b.net) - Number(a.net))
    .map((line) => ({
      key: line.clientId ?? 'none',
      name: clientName(line.clientId),
      value: Number(line.net),
      label: formatEurCents(line.net),
    }));
  const netSummary = `Net par client en ${monthLabel} : ${netItems
    .map((item) => `${item.name} ${spokenEuros(item.value)}`)
    .join(', ')}.`;

  const billed = Number(total?.billed ?? '0');
  const providerCost = Number(total?.providerCost ?? '0');
  const usageMax = Math.max(billed, providerCost, 0);

  const tabs = (
    <TableTabs<DetailTab>
      aria-label="Détail du mois"
      value={tab}
      onValueChange={setTab}
      items={[
        {
          value: 'clients',
          label: 'Détail par client',
          count: overview.data !== undefined ? rows.length : undefined,
        },
        {
          value: 'costs',
          label: 'Frais généraux',
          count: charges.data !== undefined ? chargeRows.length : undefined,
        },
      ]}
    />
  );

  return (
    <>
      <PageChrome
        crumbs={[{ label: "Vue d'ensemble" }]}
        actions={<MonthSwitcher month={month} onChange={setMonth} />}
      />
      <PageHead
        title="Vue d'ensemble"
        subtitle="Revenus, coûts et marge par client. Montants HT."
      />

      {overview.isLoading ? (
        <LoadingState />
      ) : (
        <>
          {overview.isError ? (
            <Callout variant="destructive">
              Impossible de charger les chiffres du mois. Les montants affichés ne sont pas à jour.
            </Callout>
          ) : null}

          <Row4 aria-label="Indicateurs du mois">
            <StatCard
              label="Net facturé"
              value={formatEurCents(total?.net ?? '0')}
              foot="Après ajustements"
            />
            <StatCard
              label="Abonnements"
              value={formatEurCents(total?.chargesBilled ?? '0')}
              foot={`Coût ${formatEurCents(total?.chargesCost ?? '0')}`}
            />
            <StatCard
              label="Frais généraux"
              value={formatEurCents(total?.platformCost ?? '0')}
              foot={
                charges.data !== undefined
                  ? `${activeCharges} ${activeCharges > 1 ? 'charges fixes actives' : 'charge fixe active'}`
                  : 'Charges fixes de la structure'
              }
            />
            <StatCard
              label="Marge"
              value={formatPercent(totalPct)}
              foot={`${formatEurCents(total?.margin ?? '0')} après frais généraux`}
            />
          </Row4>

          <Split21 className="items-start">
            <Card>
              <CardHead
                title="Usage IA du mois"
                description={`Facturé brut avant ajustements et coût fournisseur (tokens et SMS), ${monthLabel}, en € HT`}
                actions={
                  <ChartLegend
                    items={[
                      { label: 'Facturé', color: 'chart-2', shape: 'square' },
                      { label: 'Coût fournisseur', color: 'chart-5', shape: 'square' },
                    ]}
                  />
                }
              />
              <CardContent className="grid gap-1">
                <HBars
                  ariaLabel={`En ${monthLabel} : facturé brut ${spokenEuros(billed)}, coût fournisseur ${spokenEuros(providerCost)}.`}
                  max={usageMax}
                  items={[
                    {
                      key: 'billed',
                      name: 'Facturé',
                      value: billed,
                      label: formatEurCents(billed),
                      color: 'chart-2',
                    },
                    {
                      key: 'provider-cost',
                      name: 'Coût fournisseur',
                      value: providerCost,
                      label: formatEurCents(providerCost),
                      color: 'chart-5',
                    },
                  ]}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHead title="Net par client" description="Après ajustements, en € HT" />
              <CardContent>
                {netItems.length > 0 ? (
                  <HBars ariaLabel={netSummary} items={netItems} />
                ) : (
                  <EmptyState
                    className="py-6"
                    title="Aucune activité"
                    description="Aucun client facturé sur cette période."
                  />
                )}
              </CardContent>
            </Card>
          </Split21>

          <Card>
            {tab === 'clients' ? (
              <>
                <CardBar start={tabs} />
                <Table className="min-w-[760px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client</TableHead>
                      <TableHead kind="num">Usage</TableHead>
                      <TableHead kind="num">Coût fournisseur</TableHead>
                      <TableHead kind="num">Abonnements</TableHead>
                      <TableHead kind="num">Ajustements</TableHead>
                      <TableHead kind="num">Net</TableHead>
                      <TableHead kind="num">Marge</TableHead>
                      <TableHead kind="num">Requêtes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((line) => (
                      <TableRow key={line.clientId ?? 'none'}>
                        <TableCell kind="name">
                          {line.clientId !== null ? (
                            <Cell2
                              primary={
                                <Link
                                  className={rowLinkClass}
                                  to="/clients/$clientId"
                                  params={{ clientId: line.clientId }}
                                >
                                  {clientName(line.clientId)}
                                </Link>
                              }
                              secondary={line.clientId}
                              secondaryMono
                            />
                          ) : (
                            clientName(line.clientId)
                          )}
                        </TableCell>
                        <TableCell kind="num">{formatEurCents(line.billed)}</TableCell>
                        <TableCell kind="num">{formatEurCents(line.providerCost)}</TableCell>
                        <TableCell kind="num">{formatEurCents(line.chargesBilled)}</TableCell>
                        <TableCell kind="num">{formatEurCents(line.adjustments)}</TableCell>
                        <TableCell kind="num">{formatEurCents(line.net)}</TableCell>
                        <TableCell kind="num">
                          {formatPercent(marginPercent(line.net, line.margin))}
                        </TableCell>
                        <TableCell kind="num">{line.requestCount}</TableCell>
                      </TableRow>
                    ))}
                    {rows.length === 0 ? (
                      <TableEmptyRow colSpan={8}>Aucune activité sur cette période.</TableEmptyRow>
                    ) : null}
                  </TableBody>
                  {total !== undefined && rows.length > 0 ? (
                    <TableFooter>
                      <TableRow>
                        <TableCell>Total</TableCell>
                        <TableCell kind="num">{formatEurCents(total.billed)}</TableCell>
                        <TableCell kind="num">{formatEurCents(total.providerCost)}</TableCell>
                        <TableCell kind="num">{formatEurCents(total.chargesBilled)}</TableCell>
                        <TableCell kind="num">{formatEurCents(total.adjustments)}</TableCell>
                        <TableCell kind="num">{formatEurCents(total.net)}</TableCell>
                        <TableCell kind="num">{formatPercent(totalPct)}</TableCell>
                        <TableCell kind="num">{total.requestCount}</TableCell>
                      </TableRow>
                    </TableFooter>
                  ) : null}
                </Table>
                <CardFoot
                  start={`${rows.length} ${rows.length > 1 ? 'lignes' : 'ligne'} sur ${monthLabel}`}
                  end="Montants HT, marge totale calculée après frais généraux"
                />
              </>
            ) : (
              <PlatformChargesSection tabs={tabs} />
            )}
          </Card>
        </>
      )}
    </>
  );
}
